/**
 * /reset/:token — set a new password using the link emailed via /forgot.
 *
 * Two-field form (new password + confirm). On success, redirects to /login
 * with a "Password updated, sign in" banner.
 */

import { useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { api, extractErrorMessage } from "@/lib/api";

export function ResetPasswordPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw1.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err, "Couldn't reset — the link may have expired."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={48} />
          </div>
          <LogoWordmark size={20} />
          <h1 style={heading}>Set a new password</h1>
        </div>

        <form onSubmit={onSubmit}>
          <label style={{ display: "block" }}>
            <span style={labelStyle}>New password</span>
            <input
              type="password"
              required
              autoFocus
              minLength={8}
              placeholder="At least 8 characters"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              style={input}
              autoComplete="new-password"
            />
          </label>
          <label style={{ display: "block", marginTop: 12 }}>
            <span style={labelStyle}>Confirm new password</span>
            <input
              type="password"
              required
              minLength={8}
              placeholder="Type it again"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              style={input}
              autoComplete="new-password"
            />
          </label>

          {error && (
            <p role="alert" style={errorBanner}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...primaryBtn,
              marginTop: 16,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            {submitting ? "Updating…" : "Set new password"}
          </button>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Link to="/login" style={backLink}>
              ‹ Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- styles ----------

const wrap: CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  padding: 24,
  background: "var(--canvas)",
};

const card: CSSProperties = {
  width: "100%",
  maxWidth: 400,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 32,
};

const heading: CSSProperties = {
  fontSize: 22,
  fontWeight: 500,
  letterSpacing: "-0.2px",
  margin: "16px 0 0",
  color: "var(--body)",
};

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 6,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "9px 12px",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const primaryBtn: CSSProperties = {
  width: "100%",
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const backLink: CSSProperties = {
  fontSize: 12.5,
  color: "var(--mute)",
  textDecoration: "none",
};

const errorBanner: CSSProperties = {
  marginTop: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};
